<template>
  <div id="app">
    <div class="toolbar">
      <button class="toolbar-toggle" :class="{ expanded: toolbarExpanded }" @click="toolbarExpanded = !toolbarExpanded">
        <span class="arrow">▼</span>
        <span>{{ toolbarExpanded ? $t('toolbar.collapse') : $t('toolbar.expand') }}</span>
      </button>
      <div class="toolbar-row">
        <input
          ref="urlInput"
          class="url-input"
          :placeholder="$t('input.placeholder')"
          v-model="inputUrl"
          @keyup.enter="load"
        />
        <select v-model="inputFormat" class="format-select">
          <option value="">{{ $t('format.auto') }}</option>
          <option value="stl">.stl</option>
          <option value="obj">.obj</option>
          <option value="ply">.ply</option>
          <option value="gltf">.gltf</option>
          <option value="glb">.glb</option>
          <option value="fbx">.fbx</option>
          <option value="step">.step/.stp</option>
          <option value="iges">.iges/.igs</option>
          <option value="brep">.brep</option>
        </select>
        <button class="btn" @click="load" :disabled="!inputUrl.trim() || loadingRef">{{ $t('btn.add') }}</button>
        <button class="btn btn-secondary" @click="clearAll" :disabled="modelList.length === 0">{{ $t('btn.clearAll') }}</button>
        <button class="btn btn-outline" @click="resetView" :disabled="modelList.length === 0">{{ $t('btn.resetView') }}</button>
        <button class="btn btn-outline" @click="exportHTML" :disabled="modelList.length === 0">{{ $t('btn.exportHTML') }}</button>
        <button class="btn btn-outline" @click="exportGLB" :disabled="modelList.length === 0">{{ $t('btn.exportGLB') }}</button>
        <select class="lang-select" v-model="$i18n.locale" @change="onLangChange">
          <option value="zh">中文</option>
          <option value="en">English</option>
          <option value="de">Deutsch</option>
        </select>
      </div>
      <div class="toolbar-row" v-if="modelList.length > 0">
        <span class="model-tag" v-for="(m, i) in modelList" :key="i" :class="{ 'model-tag-hidden': !m.visible }">
          <span class="model-tag-eye" @click="toggleModel(i)">{{ m.visible ? '👁' : '👁‍🗨' }}</span>
          <span class="model-tag-name" @click="toggleModel(i)">{{ m.name }}</span>
          <span class="model-tag-focus" @click="focusModel(i)" title="聚焦查看">◎</span>
          <span class="model-tag-info">{{ m.format.toUpperCase() }} | {{ m.vertexCount.toLocaleString() }}{{ $t('model.vertices') }} | {{ m.faceCount.toLocaleString() }}{{ $t('model.faces') }}</span>
          <span v-if="m.volume > 0" class="model-tag-meas">
            {{ formatDimensions(m.dimensions, m.unit) }} | V={{ formatVolume(m.volume, m.unit) }} |
            SA={{ formatSurfaceArea(m.surfaceArea, m.unit) }} |
            <template v-if="m.hollow">
              <span class="inline-label">{{ $t('weight.thickness') }}</span>
              <input type="number" v-model.number="m.thickness" class="thickness-input" min="0.1" step="0.1" />
              <span class="inline-label">{{ $t('weight.mm') }}</span>
            </template>
            W={{ formatWeight((m.hollow ? hollowVolumeCm3(m.surfaceArea, m.thickness, m.unit) : volumeToCm3(m.volume, m.unit)) * densityTable[m.densityKey]) }}
            <select v-model="m.densityKey" class="density-select">
              <option v-for="k in densityNames" :key="k" :value="k">{{ $t('material.' + k) }}</option>
            </select>
            <label class="hollow-label" :class="{ active: m.hollow }" :title="m.hollow ? '按空心壁厚计算重量' : '按实心体积计算重量'">
              <input type="checkbox" v-model="m.hollow" class="sr-only" />
              {{ m.hollow ? $t('weight.hollow') : $t('weight.solid') }}
            </label>
          </span>
          <span class="model-tag-remove" @click="removeModel(i)">&times;</span>
        </span>
      </div>
      <div class="toolbar-row" :class="{ collapsed: !toolbarExpanded }">
        <span class="status" :class="statusClass">{{ statusText }}</span>
        <span class="separator"></span>
        <button class="btn btn-outline btn-small" @click="undo" :disabled="!canUndo">{{ $t('btn.undo') }}</button>
        <button class="btn btn-outline btn-small" @click="redo" :disabled="!canRedo">{{ $t('btn.redo') }}</button>
        <span class="separator"></span>
        <label class="toggle-label">
          <input type="checkbox" v-model="clipEnabled" @change="updateClip" :disabled="modelList.length === 0" />
          <span>{{ $t('clip.cut') }}</span>
        </label>
        <select v-model="clipAxis" @change="updateClip" class="mini-select" :disabled="!clipEnabled || modelList.length === 0">
          <option value="x">{{ $t('clip.xAxis') }}</option>
          <option value="y">{{ $t('clip.yAxis') }}</option>
          <option value="z">{{ $t('clip.zAxis') }}</option>
        </select>
        <input
          type="range"
          class="clip-slider"
          min="-5"
          max="5"
          step="0.01"
          v-model.number="clipPosition"
          @input="updateClip"
          :disabled="!clipEnabled || modelList.length === 0"
        />
        <span class="slider-value">{{ clipPosition.toFixed(2) }}</span>
        <label class="toggle-label">
          <input type="checkbox" v-model="clipShowCap" @change="updateClip" :disabled="!clipEnabled || modelList.length === 0" />
          <span>{{ $t('clip.section') }}</span>
        </label>
        <span class="separator"></span>
        <button
          class="btn btn-outline btn-small"
          @click="measuring = !measuring; if (measuring) annotating = false"
          :class="{ active: measuring }"
          :disabled="modelList.length === 0"
        >{{ $t('btn.measure') }}</button>
        <button
          class="btn btn-outline btn-small"
          @click="clearMeasurements"
          :disabled="modelList.length === 0"
          v-if="measuring"
        >{{ $t('btn.clear') }}</button>
        <span class="separator"></span>
        <button
          class="btn btn-outline btn-small"
          @click="annotating = !annotating; if (annotating) measuring = false"
          :class="{ active: annotating }"
          :disabled="modelList.length === 0"
        >{{ $t('btn.annotate') }}</button>
        <span class="separator"></span>
        <select v-model="splitTarget" class="mini-select" :disabled="modelList.length === 0">
          <option :value="-1">{{ $t('btn.selectSplit') }}</option>
          <option v-for="(m, i) in modelList" :key="'sp-'+i" :value="i">{{ m.name }}</option>
        </select>
        <button
          class="btn btn-outline btn-small"
          @click="doSplit"
          :disabled="splitTarget < 0"
        >{{ $t('btn.split') }}</button>
        <button
          v-if="canUndo"
          class="btn btn-outline btn-small"
          @click="undo"
        >{{ $t('btn.restore') }}</button>
      </div>
      <div class="toolbar-row" :class="{ collapsed: !toolbarExpanded }" v-if="annEditList.length > 0">
        <span class="ann-tag" v-for="ann in annEditList" :key="ann.id">
          <textarea class="ann-input" :ref="'annInput' + ann.id" v-model="ann.text" @input="updateAnnText(ann); autoResize($event)" rows="1"></textarea>
          <span class="ann-remove" @click="removeAnnotation(ann.id)">&times;</span>
        </span>
      </div>
    </div>
    <div
      class="viewer-body"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div v-if="dropHint" class="drop-hint">
        <div class="drop-hint-inner">{{ $t('dropHint.text') }}</div>
      </div>
      <div class="main-view">
        <ThreeRenderer
          ref="renderer"
          width="100%"
          height="100%"
          :backgroundColor="0x1a1a2e"
          :autoCenter="true"
          :autoScale="true"
          :measuring="measuring"
          :annotating="annotating"
          @scene-ready="onSceneReady"
          @model-loaded="onModelLoaded"
          @model-error="onModelError"
          @annotation-add="onAnnotationAdd"
        />
      </div>
      <div class="bottom-views">
        <OrthoViewer
          ref="orthoLeft"
          side="left"
          :scene="sharedScene"
          :target="orthoTarget"
          :backgroundColor="0x12122a"
        />
        <OrthoViewer
          ref="orthoRight"
          side="right"
          :scene="sharedScene"
          :target="orthoTarget"
          :backgroundColor="0x12122a"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import * as THREE from 'three'
import ThreeRenderer from './components/ThreeRenderer.vue'
import OrthoViewer from './components/OrthoViewer.vue'
import { LightingManager } from './core/LightingManager'
import { exportModelAsHTML, exportModelAsGLB, exportAnnotationsAsJSON, exportOriginalWithAnnotations, ExportMeasurements } from './core/ModelExporter'
import type { SceneReadyPayload, ModelLoadResult, ModelFormat, ClipConfig } from './core/types'
import { DENSITY_TABLE, guessUnitFromFormat, volumeToCm3, dimToCm, saToCm2, hollowVolumeCm3, formatSurfaceArea, formatDimensions, formatVolume, formatWeight, computeMeasurements } from './core/MeasurementUtils'
import type { UnitSystem } from './core/MeasurementUtils'
import i18n from './i18n'

const DENSITY_NAMES = Object.keys(DENSITY_TABLE)

interface ModelEntry {
  name: string
  format: ModelFormat
  vertexCount: number
  faceCount: number
  visible: boolean
  volume: number
  surfaceArea: number
  dimensions: { x: number; y: number; z: number }
  densityKey: string
  unit: UnitSystem
  hollow: boolean
  thickness: number
  originalBlob?: Blob
}

interface AnnEdit {
  id: string
  text: string
}

interface HistorySnapshot {
  groups: THREE.Group[]
  entries: ModelEntry[]
}

export default Vue.extend({
  name: 'App',
  components: { ThreeRenderer, OrthoViewer },

  data() {
    return {
      inputUrl: '',
      inputFormat: '' as ModelFormat | '',
      statusText: i18n.t('status.idle') as string,
      statusClass: 'idle' as 'idle' | 'loading' | 'success' | 'error',
      loadingRef: false,
      sharedScene: null as THREE.Scene | null,
      modelList: [] as ModelEntry[],
      densityNames: DENSITY_NAMES,
      densityTable: DENSITY_TABLE,
      clipEnabled: false,
      clipAxis: 'x' as 'x' | 'y' | 'z',
      clipPosition: 0,
      clipShowCap: true,
      dropHint: false,
      measuring: false,
      annotating: false,
      pendingBlob: null as Blob | null,
      pendingBlobName: '',
      annEditList: [] as AnnEdit[],
      orthoTarget: new THREE.Vector3(0, 0, 0),
      history: [] as HistorySnapshot[],
      historyIndex: -1,
      historyBusy: false,
      sceneManager: null as import('./core/SceneManager').SceneManager | null,
      splitTarget: -1,
      toolbarExpanded: false,
    }
  },

  computed: {
    canUndo(): boolean {
      return this.historyIndex > 0 && !this.historyBusy
    },
    canRedo(): boolean {
      return this.historyIndex < this.history.length - 1 && !this.historyBusy
    },
  },

  methods: {
    onLangChange() {
      localStorage.setItem('locale', this.$i18n.locale)
      this.statusText = this.$t('status.idle') as string
    },

    async load() {
      const url = this.inputUrl.trim()
      if (!url) return
      this.loadingRef = true
      this.statusText = this.$t('status.loading') as string
      this.statusClass = 'loading'
      try {
        const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
        this.saveHistory()
        await renderer.addModel(url, this.inputFormat || undefined)
        this.inputUrl = ''
        this.inputFormat = ''
      } catch (e) {
        console.error('load failed', e)
      } finally {
        this.loadingRef = false
      }
    },

    toggleModel(index: number) {
      this.modelList[index].visible = !this.modelList[index].visible
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      renderer?.setModelVisible(index, this.modelList[index].visible)
    },

    saveHistory() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      const groups = renderer?.getModelGroups() || []
      const snap: HistorySnapshot = { groups: [...groups], entries: JSON.parse(JSON.stringify(this.modelList)) }
      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(snap)
      this.historyIndex++
    },

    undo() {
      if (!this.canUndo) return
      this.historyBusy = true
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      if (!renderer) return
      this.historyIndex--
      this.restoreSnapshot(this.history[this.historyIndex])
      this.historyBusy = false
    },

    redo() {
      if (!this.canRedo) return
      this.historyBusy = true
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      if (!renderer) return
      this.historyIndex++
      this.restoreSnapshot(this.history[this.historyIndex])
      this.historyBusy = false
    },

    restoreSnapshot(snap: HistorySnapshot) {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      if (!renderer || !this.sceneManager) return
      for (const g of renderer.getModelGroups()) {
        this.sceneManager.remove(g)
      }
      renderer.clearMeasurements?.()
      for (const g of snap.groups) {
        this.sceneManager.add(g)
      }
      this.modelList = JSON.parse(JSON.stringify(snap.entries))
      this.$nextTick(() => {
        renderer['models'] = snap.groups
        renderer.syncInitialPositions()
        this.sceneManager?.fitToObjects(snap.groups)
      })
    },

    clearAll() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      this.saveHistory()
      renderer?.clearModels()
      renderer?.clearMeasurements?.()
      renderer?.clearAnnotations?.()
      this.modelList = []
      this.clipEnabled = false
      this.clipPosition = 0
      this.measuring = false
      this.annotating = false
      this.annEditList = []
      this.statusText = this.$t('status.cleared') as string
      this.statusClass = 'idle'
    },

    removeModel(index: number) {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      this.saveHistory()
      renderer?.removeModel(index)
      this.modelList.splice(index, 1)
      if (this.modelList.length === 0) {
        this.clipEnabled = false
        this.clipPosition = 0
        this.annotating = false
      }
    },

    resetView() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      renderer?.resetView()
    },

    focusModel(index: number) {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      renderer?.focusModel(index)
    },

    doSplit() {
      const index = this.splitTarget
      if (index < 0) return
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      if (!renderer) return
      this.saveHistory()
      const result = renderer.splitModel(index)
      if (!result) {
        this.statusText = this.$t('status.noSplitParts') as string
        this.statusClass = 'error'
        setTimeout(() => { this.statusClass = 'idle' }, 2000)
        return
      }
      const original = this.modelList[index]
      const entries = result.groups.map((group, i) => {
        const meas = computeMeasurements(group)
        let faceCount = 0
        group.traverse(c => {
          if (c instanceof THREE.Mesh && c.geometry) {
            const g = c.geometry
            faceCount += g.index ? g.index.count / 3 : g.attributes.position.count / 3
          }
        })
        return {
          name: original.name.replace(/\.[^.]+$/, '') + ' ' + this.$t('model.part') + (i + 1),
          format: original.format,
          vertexCount: 0,
          faceCount: Math.floor(faceCount),
          visible: true,
          volume: meas.volume,
          surfaceArea: meas.surfaceArea,
          dimensions: meas.dimensions,
          densityKey: original.densityKey,
          unit: original.unit,
          hollow: false,
          thickness: original.thickness,
        }
      })
      this.modelList.splice(index, 1, ...entries)
      this.splitTarget = -1
      this.statusText = this.$t('status.splitDone', { count: result.count }) as string
      this.statusClass = 'success'
      setTimeout(() => { this.statusClass = 'idle' }, 3000)
    },

    async exportHTML() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      const allGroups = renderer?.getModelGroups() || []
      const visibleEntries = this.modelList.filter(m => m.visible)
      if (visibleEntries.length === 0) return
      const merged = new THREE.Group()
      for (let i = 0; i < this.modelList.length; i++) {
        if (this.modelList[i].visible && allGroups[i]) {
          merged.add(allGroups[i].clone())
        }
      }
      const measurements: ExportMeasurements[] = visibleEntries.map(m => ({
        name: m.name,
        dims: formatDimensions(m.dimensions, m.unit),
        volume: formatVolume(m.volume, m.unit),
        surfaceArea: formatSurfaceArea(m.surfaceArea, m.unit),
        weight: formatWeight((m.hollow ? hollowVolumeCm3(m.surfaceArea, m.thickness, m.unit) : volumeToCm3(m.volume, m.unit)) * this.densityTable[m.densityKey]),
      }))
      const annotations = renderer?.getAnnotations?.() || []
      const annData = annotations.map((a: { position: { x: number; y: number; z: number }; text: string }) => ({
        x: a.position.x, y: a.position.y, z: a.position.z, text: a.text,
      }))
      try {
        this.statusText = this.$t('status.exporting') as string
        this.statusClass = 'loading'
        await exportModelAsHTML(merged, measurements, annData, 'model-viewer.html', this.$i18n.locale)
        this.statusText = this.$t('status.exportSuccess') as string
        this.statusClass = 'success'
      } catch (e) {
        this.statusText = `${this.$t('status.exportFailed')}: ${e instanceof Error ? e.message : 'Unknown error'}`
        this.statusClass = 'error'
      }
    },

    async exportGLB() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      const allGroups = renderer?.getModelGroups() || []
      const visibleEntries = this.modelList.filter(m => m.visible)
      if (visibleEntries.length === 0) return
      const merged = new THREE.Group()
      for (let i = 0; i < this.modelList.length; i++) {
        if (this.modelList[i].visible && allGroups[i]) {
          merged.add(allGroups[i].clone())
        }
      }
      const annotations = renderer?.getAnnotations?.() || []
      const annData = annotations.map((a: { position: { x: number; y: number; z: number }; text: string }) => ({
        x: a.position.x, y: a.position.y, z: a.position.z, text: a.text,
      }))
      try {
        this.statusText = this.$t('status.exporting') as string
        this.statusClass = 'loading'
        await exportModelAsGLB(merged, annData, 'annotated-model.glb')
        this.statusText = this.$t('status.exportSuccess') as string
        this.statusClass = 'success'
      } catch (e) {
        this.statusText = `${this.$t('status.exportFailed')}: ${e instanceof Error ? e.message : 'Unknown error'}`
        this.statusClass = 'error'
      }
    },

    exportAnnJSON() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      const annotations = renderer?.getAnnotations?.() || []
      if (annotations.length === 0) return
      const annData = annotations.map((a: { position: { x: number; y: number; z: number }; text: string }) => ({
        x: a.position.x, y: a.position.y, z: a.position.z, text: a.text,
      }))
      try {
        exportAnnotationsAsJSON(annData, 'annotations.json')
        this.statusText = this.$t('status.exportSuccess') as string
        this.statusClass = 'success'
      } catch (e) {
        this.statusText = `${this.$t('status.exportFailed')}: ${e instanceof Error ? e.message : 'Unknown error'}`
        this.statusClass = 'error'
      }
    },

    async exportZip() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      const annotations = renderer?.getAnnotations?.() || []
      if (annotations.length === 0) return
      const entry = this.modelList[0]
      if (!entry?.originalBlob) {
        this.statusText = this.$t('status.noOriginalFile') as string
        this.statusClass = 'error'
        return
      }
      const annData = annotations.map((a: { position: { x: number; y: number; z: number }; text: string }) => ({
        x: a.position.x, y: a.position.y, z: a.position.z, text: a.text,
      }))
      try {
        this.statusText = this.$t('status.exporting') as string
        this.statusClass = 'loading'
        await exportOriginalWithAnnotations(entry.originalBlob, entry.name, annData)
        this.statusText = this.$t('status.exportSuccess') as string
        this.statusClass = 'success'
      } catch (e) {
        this.statusText = `${this.$t('status.exportFailed')}: ${e instanceof Error ? e.message : 'Unknown error'}`
        this.statusClass = 'error'
      }
    },

    updateClip() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      if (!renderer || this.modelList.length === 0) return
      const config: ClipConfig = {
        enabled: this.clipEnabled,
        position: this.clipPosition,
        axis: this.clipAxis,
        showCap: this.clipShowCap,
      }
      renderer.applyClip(config)
    },

    clearMeasurements() {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      renderer?.clearMeasurements?.()
    },

    onAnnotationAdd(pt: THREE.Vector3) {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      if (!renderer) return
      const ann = renderer.addAnnotationAt(pt, '')
      if (ann) {
        this.annEditList.push({ id: ann.id, text: ann.text })
        this.statusText = this.$t('status.annotationHint') as string
        this.statusClass = 'success'
      }
    },

    updateAnnText(ann: AnnEdit) {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      renderer?.updateAnnotation(ann.id, ann.text)
    },

    removeAnnotation(id: string) {
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      renderer?.removeAnnotation(id)
      this.annEditList = this.annEditList.filter(a => a.id !== id)
    },

    autoResize(e: Event) {
      const el = e.target as HTMLTextAreaElement
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    },

    resizeAllAnns() {
      this.$nextTick(() => {
        for (const ann of this.annEditList) {
          const ref = this.$refs['annInput' + ann.id] as HTMLTextAreaElement
          if (ref) {
            ref.style.height = 'auto'
            ref.style.height = ref.scrollHeight + 'px'
          }
        }
      })
    },

    onSceneReady(payload: SceneReadyPayload) {
      const { scene, manager } = payload
      this.sceneManager = manager
      new LightingManager(scene)
      const grid = new THREE.GridHelper(20, 20, 0x444466, 0x222244)
      scene.add(grid)
      this.sharedScene = scene

      manager.onFrame(() => {
        if (manager.controls) {
          this.orthoTarget.copy(manager.controls.target)
        }
      })

      this.$nextTick(() => {
        const left = this.$refs.orthoLeft as InstanceType<typeof OrthoViewer>
        const right = this.$refs.orthoRight as InstanceType<typeof OrthoViewer>
        const lEr = left?.getExternalRenderer()
        const rEr = right?.getExternalRenderer()
        if (lEr && rEr) {
          manager.addExternalRenderer(lEr)
          manager.addExternalRenderer(rEr)
        }
      })
    },

    onModelLoaded(result: ModelLoadResult) {
      try {
        const name = result.url.split('/').pop() || result.url.split('\\').pop() || 'unknown'
        const meas = result.measurements
        const unit = guessUnitFromFormat(result.format)
        this.modelList.push({
          name,
          format: result.format,
          vertexCount: result.vertexCount,
          faceCount: result.faceCount,
          visible: true,
          volume: meas?.volume || 0,
          surfaceArea: meas?.surfaceArea || 0,
          dimensions: meas?.dimensions || { x: 0, y: 0, z: 0 },
          densityKey: Object.keys(this.densityTable)[0],
          unit,
          hollow: false,
          thickness: 2,
          originalBlob: this.pendingBlob || undefined,
        })
        this.pendingBlob = null
        this.pendingBlobName = ''
        this.statusClass = 'success'
        this.statusText = this.$t('status.loadSuccess') as string
        this.$nextTick(() => {
          const left = this.$refs.orthoLeft as InstanceType<typeof OrthoViewer>
          const right = this.$refs.orthoRight as InstanceType<typeof OrthoViewer>
          left?.fitToScene()
          right?.fitToScene()
        })
      } catch (e) {
        console.error('onModelLoaded error', e)
      } finally {
        this.loadingRef = false
      }
    },

    onModelError(err: Error) {
      this.loadingRef = false
      this.statusClass = 'error'
      this.statusText = `${this.$t('status.loadFailed')}: ${err.message}`
    },

    onDragOver() {
      this.dropHint = true
    },

    onDragLeave() {
      this.dropHint = false
    },

    async onDrop(e: DragEvent) {
      this.dropHint = false
      const files = e.dataTransfer?.files
      if (!files || files.length === 0) return

      this.loadingRef = true
      this.saveHistory()
      const renderer = this.$refs.renderer as InstanceType<typeof ThreeRenderer>
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const ext = file.name.split('.').pop()?.toLowerCase()
        if (ext !== 'stl' && ext !== 'obj' && ext !== 'ply' && ext !== 'gltf' && ext !== 'glb' && ext !== 'fbx' && ext !== 'step' && ext !== 'stp' && ext !== 'iges' && ext !== 'igs' && ext !== 'brep') {
          this.statusClass = 'error'
          this.statusText = `${file.name}: ${this.$t('status.unsupportedFormat')}`
          continue
        }
        const blobUrl = URL.createObjectURL(file)
        this.statusText = `${this.$t('status.loadingFile')} ${file.name}...`
        this.statusClass = 'loading'
        this.pendingBlob = file
        this.pendingBlobName = file.name
        try {
          await renderer.addModel(blobUrl, ext as ModelFormat)
        } catch (e) {
          const msg = e instanceof Error ? e.message : '未知错误'
          this.statusText = `${file.name}: ${msg}`
          this.statusClass = 'error'
        }
      }
      this.loadingRef = false
    },

    formatDimensions(dim: { x: number; y: number; z: number }, unit: string): string {
      const scale: Record<string, number> = { mm: 0.1, cm: 1, m: 100 }
      const s = scale[unit] ?? 1
      const fmt = (v: number) => (v * s >= 10) ? (v * s).toFixed(1) : (v * s).toFixed(2)
      return `${fmt(dim.x)} × ${fmt(dim.y)} × ${fmt(dim.z)}`
    },
    formatVolume(vol: number, unit: string): string {
      const scale: Record<string, number> = { mm: 0.001, cm: 1, m: 1_000_000 }
      const cm3 = vol * (scale[unit] ?? 1)
      if (cm3 >= 1000000) return `${(cm3 / 1000000).toFixed(2)} m³`
      if (cm3 >= 1) return `${cm3.toFixed(2)} cm³`
      return `${(cm3 * 1000).toFixed(2)} mm³`
    },
    formatWeight(w: number): string {
      if (w >= 1000) return `${(w / 1000).toFixed(2)} kg`
      if (w >= 1) return `${w.toFixed(1)} g`
      return `${(w * 1000).toFixed(1)} mg`
    },
    volumeToCm3(vol: number, unit: string): number {
      const scale: Record<string, number> = { mm: 0.001, cm: 1, m: 1_000_000 }
      return vol * (scale[unit] ?? 1)
    },
    hollowVolumeCm3(sa: number, thickness: number, unit: string): number {
      const scale: Record<string, number> = { mm: 0.01, cm: 1, m: 10_000 }
      const saCm2 = sa * (scale[unit] ?? 1)
      return saCm2 * thickness * 0.1
    },
    formatSurfaceArea(sa: number, unit: string): string {
      const scale: Record<string, number> = { mm: 0.01, cm: 1, m: 10_000 }
      const cm2 = sa * (scale[unit] ?? 1)
      if (cm2 >= 10000) return `${(cm2 / 10000).toFixed(2)} m²`
      if (cm2 >= 1) return `${cm2.toFixed(2)} cm²`
      return `${(cm2 * 100).toFixed(2)} mm²`
    },
  },
})
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  background: #0f0f1a;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #e0e0e0;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}
#app {
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.toolbar {
  padding: 12px 20px;
  background: #1a1a2e;
  border-bottom: 1px solid #2a2a3e;
  flex-shrink: 0;
}
.toolbar-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.toolbar-row + .toolbar-row {
  margin-top: 8px;
}
.url-input {
  flex: 1;
  min-width: 200px;
  padding: 8px 14px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #0f0f1a;
  color: #e0e0e0;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}
.url-input:focus { border-color: #00aaff; }
.url-input::placeholder { color: #555; }
.format-select {
  padding: 8px 14px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #0f0f1a;
  color: #e0e0e0;
  font-size: 14px;
  outline: none;
  cursor: pointer;
}
.format-select:focus { border-color: #00aaff; }
.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #00aaff;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;
}
.btn:hover { opacity: 0.85; }
.btn:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-secondary {
  background: #333;
  color: #ccc;
}
.btn-secondary:hover { background: #444; }
.btn-outline {
  background: transparent;
  border: 1px solid #00aaff;
  color: #00aaff;
}
.btn-outline:hover { background: rgba(0, 170, 255, 0.1); }
.btn-outline.active { background: #00aaff; color: #fff; }
.btn-small { padding: 4px 10px; font-size: 12px; }
.lang-select {
  padding: 6px 10px;
  border: 1px solid #00aaff;
  border-radius: 6px;
  background: transparent;
  color: #00aaff;
  font-size: 12px;
  outline: none;
  cursor: pointer;
}
.lang-select option { background: #1a1a2e; color: #e0e0e0; }
.icon-btn { display: inline-flex; align-items: center; gap: 4px; min-width: 36px; }
.icon-btn svg { flex-shrink: 0; }
.status {
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 4px;
}
.status.idle { color: #888; }
.status.loading { color: #ffaa00; }
.status.success { color: #00cc66; }
.status.error { color: #ff4444; }
.separator {
  width: 1px;
  height: 24px;
  background: #333;
  flex-shrink: 0;
}
.toggle-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #aaa;
  cursor: pointer;
  user-select: none;
}
.toggle-label input[type="checkbox"] {
  accent-color: #00aaff;
}
.mini-select {
  padding: 4px 8px;
  border: 1px solid #333;
  border-radius: 4px;
  background: #0f0f1a;
  color: #e0e0e0;
  font-size: 13px;
  outline: none;
  cursor: pointer;
}
.mini-select:focus { border-color: #00aaff; }
.clip-slider {
  width: 120px;
  accent-color: #00aaff;
  cursor: pointer;
}
.clip-slider:disabled { opacity: 0.4; cursor: not-allowed; }
.slider-value {
  font-size: 12px;
  color: #888;
  min-width: 36px;
  text-align: center;
}
.model-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 8px;
  background: #0f0f1a;
  border: 1px solid #333;
  border-radius: 6px;
  font-size: 12px;
  cursor: default;
}
.model-tag-hidden {
  opacity: 0.4;
  border-color: #222;
}
.model-tag-eye {
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  user-select: none;
}
.model-tag-name {
  color: #00aaff;
  font-weight: 600;
  cursor: pointer;
}
.model-tag-name:hover {
  text-decoration: underline;
}
.model-tag-info {
  color: #666;
}
.model-tag-remove {
  color: #666;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
}
.model-tag-remove:hover { color: #ff4444; }
.model-tag-focus {
  color: #555;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
}
.model-tag-focus:hover { color: #00aaff; }
.model-tag-meas {
  color: #888;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-wrap: wrap;
}
.hollow-label {
  cursor: pointer;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid #444;
  color: #aaa;
  font-size: 10px;
  user-select: none;
}
.hollow-label.active { border-color: #00aaff; color: #00aaff; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
.thickness-input {
  width: 42px;
  padding: 1px 3px;
  border: 1px solid #444;
  border-radius: 3px;
  background: #1a1a2e;
  color: #ccc;
  font-size: 10px;
  text-align: center;
  outline: none;
}
.thickness-input:focus { border-color: #00aaff; }
.inline-label { color: #666; font-size: 10px; }
.density-select {
  margin-left: 2px;
  padding: 1px 4px;
  border: 1px solid #444;
  border-radius: 3px;
  background: #1a1a2e;
  color: #aaa;
  font-size: 11px;
  outline: none;
  cursor: pointer;
  max-width: 120px;
}
.density-select:focus { border-color: #00aaff; }
.ann-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: #0f0f1a;
  border: 1px solid #335;
  border-radius: 4px;
  font-size: 12px;
}
.ann-input {
  width: 120px;
  min-height: 22px;
  max-height: 80px;
  padding: 2px 6px;
  border: 1px solid #444;
  border-radius: 3px;
  background: #1a1a2e;
  color: #33aaff;
  font-size: 12px;
  font-family: inherit;
  outline: none;
  resize: none;
  overflow: hidden;
  line-height: 1.3;
}
.ann-input:focus { border-color: #33aaff; }
.ann-remove {
  color: #666;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
}
.ann-remove:hover { color: #ff4444; }
.viewer-body {
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px;
}
.main-view {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #2a2a3e;
}
.bottom-views {
  display: flex;
  gap: 6px;
  height: 160px;
  flex-shrink: 0;
}
.bottom-views > * {
  flex: 1;
  min-width: 0;
}
.drop-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 170, 255, 0.08);
  border: 2px dashed #00aaff;
  z-index: 20;
  pointer-events: none;
}
.drop-hint-inner {
  padding: 16px 32px;
  background: rgba(0, 170, 255, 0.15);
  border: 1px solid #00aaff;
  border-radius: 12px;
  color: #00aaff;
  font-size: 16px;
  white-space: nowrap;
}

.toolbar-toggle {
  display: none;
}

@media (max-width: 768px) {
  .toolbar {
    padding: 8px 10px;
  }

  .toolbar-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 6px;
    margin-bottom: 6px;
    background: #2a2a3e;
    border: none;
    border-radius: 6px;
    color: #aaa;
    font-size: 13px;
    cursor: pointer;
    gap: 6px;
  }
  .toolbar-toggle .arrow {
    transition: transform 0.2s;
    font-size: 10px;
  }
  .toolbar-toggle.expanded .arrow {
    transform: rotate(180deg);
  }

  .toolbar-row {
    gap: 6px;
  }

  .toolbar-row.collapsed {
    display: none;
  }

  .url-input {
    min-width: 0;
    width: 100%;
    padding: 10px 12px;
    font-size: 16px;
  }

  .format-select {
    padding: 10px 8px;
    font-size: 16px;
  }

  .btn {
    padding: 10px 14px;
    font-size: 13px;
  }

  .btn-small {
    padding: 8px 10px;
    font-size: 12px;
  }

  .model-tag {
    font-size: 11px;
    gap: 4px;
    padding: 3px 6px;
    flex-wrap: wrap;
  }

  .model-tag-info {
    display: none;
  }

  .model-tag-meas {
    font-size: 10px;
  }

  .density-select {
    max-width: 80px;
  }

  .clip-slider {
    width: 80px;
  }

  .separator {
    height: 20px;
  }

  .toggle-label {
    font-size: 12px;
  }

  .mini-select {
    padding: 6px 6px;
    font-size: 12px;
  }

  .viewer-body {
    padding: 4px;
    gap: 4px;
  }

  .bottom-views {
    display: none;
  }

  .drop-hint-inner {
    font-size: 13px;
    padding: 10px 16px;
    white-space: normal;
    text-align: center;
  }

  .ann-input {
    width: 80px;
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .toolbar-row {
    gap: 4px;
  }

  .btn {
    padding: 8px 10px;
    font-size: 12px;
  }

  .btn-small {
    padding: 6px 8px;
    font-size: 11px;
  }
}
</style>
